import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import {
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaChevronLeft,
} from "react-icons/fa";
import {
  MdOutlineCheckBox,
  MdDriveFileMoveOutline,
  MdSelectAll,
} from "react-icons/md";
import {
  FiCopy,
  FiPlus,
  FiTag,
  FiX,
  FiUpload,
  FiFolder,
  FiFolderPlus,
  FiChevronDown,
  FiZoomIn,
  FiZoomOut,
  FiUploadCloud,
  FiChevronRight,
  FiChevronLeft,
  FiUser,
  FiDownload,
  FiMoreHorizontal,
  FiArchive,
  FiLink,
  FiBox,
} from "react-icons/fi";
import {
  MdAutoMode,
  MdOutlinePermMedia,
  MdSort,
  MdDeleteForever,
  MdDeleteOutline,
  MdRestoreFromTrash,
} from "react-icons/md";
import { LuLoaderCircle, LuSparkles } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoTag } from "react-icons/go";
import { CgArrowsExchange } from "react-icons/cg";

import Button from "../ui/Button";
import AssetEditorModal from "../modals/AssetEditorModal";
import FolderEditorModal from "../modals/FolderEditorModal";
import FolderPickerModal from "../modals/FolderPickerModal";
import ConfirmModal from "../modals/ConfirmModal";
import AssetsListContainer from "@/components/AssetsList";
import AssetsListAssetPreviewContainer from "@/components/AssetsListAssetPreview";
import PageTitle from "@/components/ui/PageTitle";
import AssetPreviewModal from "../modals/AssetPreviewModal";
import CommentsEditorModal from "../modals/CommentsEditorModal";
import TagsModal from "../modals/TagsModal";
import CharacterAssetsModal from "../modals/CharacterAssetsModal";
import BulkRenameModal from "../modals/BulkRenameModal";
import Loader from "../loader";
import { Input } from "../ui/shadcn/input";
import { SketchEditor } from "../SketchEditor";
import { DragPreviewProvider } from "../ui/DragPreview";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "../ui/shadcn/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/shadcn/tooltip";

import { useModal } from "@/contexts/ModalContext";
import { useShow } from "@/contexts/ShowContext";
import { useAssetsList } from "@/contexts/AssetsListContext";
import useFileUpload from "@/hooks/useFileUpload";
import useAddAssetToList from "@/hooks/useAddAssetToList";
import { useWebSocket } from "@/hooks/useWebSocket";
import useFileDownload from "@/hooks/useFileDownload";
import useAssetsDownload from "@/hooks/useAssetsDownload";
import useKagamiBridge from "@/hooks/useKagamiBridge";

import AssetItem from "./AssetItem";
import FolderCard from "./FolderCard";
import TaskCard from "./TaskCard";
import BreadcrumbSegment from "./BreadcrumbSegment";
import { useHandleRefresh } from "./hooks/useHandleRefresh";
import AiModelInput from "../AiModelInput";
import TasksSidebarPanel, {
  useTasksSidebar,
  TasksSidebarToggle,
} from "./TasksSidebar";
import {
  ASSET_DRAG_MIME,
  FOLDER_DRAG_MIME,
  ASSET_SORT_DROPDOWN_OPTIONS,
  DEFAULT_ASSET_SORT,
} from "./constants";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/shadcn/resizable";

const MIN_GRID_COLS = 1;
const MAX_GRID_COLS = 8;

const BYTEDANCE_DRAFT_MODEL_ID = "6abe153dc3a9191861dbc7fa";

const clampZoom = (z) => {
  const n = Number(z);
  if (!Number.isFinite(n)) return MIN_GRID_COLS;
  return Math.min(MAX_GRID_COLS, Math.max(MIN_GRID_COLS, Math.round(n)));
};

// Convert a comma-separated MIME pattern list (e.g. 'image/*,video/*') into
// the bare media-type prefixes the backend expects (e.g. 'image,video').
const toMediaPrefixes = (mediaTypes) => {
  if (!mediaTypes) return [];
  return mediaTypes
    .split(",")
    .map((t) => t.trim().split("/")[0])
    .filter(Boolean);
};

export default function AssetsEditor({
  folder: folderProp,
  title = "Assets",
  mediaTypes = "image/*,video/*,audio/*",
  zoom: initialZoom = 5,
  page: initialPage = 1,
  categories: initialCategories,
  status: initialStatus,
  media: initialMedia,
  archived: initialArchived = false,
  characterFilter: initialCharacterFilter = "",
  sort: initialSort = DEFAULT_ASSET_SORT,
  pageSize: initialPageSize = 20,
  allowUpload = true,
  allowSelect = true,
  selectMode: initialSelectMode = false,
  allowSelectFolder = true,
  showAssetsList = false,
  presetGeneration,
  onSelectedItemsChange,
  onFolderChange,
  onCategoriesChange,
  onStatusChange,
  onMediaChange,
  onArchivedChange,
  onCharacterFilterChange,
  onSortChange,
  onPageChange,
  onZoomChange,
}) {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ancestors, setAncestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("");
  const { show, commitBulkRename } = useShow();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { uploadFile, uploadProgress, isUploading } = useFileUpload();
  const { downloadFile } = useFileDownload();
  const { addAssetToList } = useAddAssetToList();
  const { removeEntriesForAsset } = useAssetsList();
  const { runTask } = useKagamiBridge();
  const {
    downloadAssets,
    downloadFolder,
    exporting: zipExporting,
  } = useAssetsDownload(show?._id);
  const [isDragOver, setIsDragOver] = useState(false);
  // Imperative handle to AiModelInput so per-asset actions (e.g. "Recall
  // settings") can preset the model + payload and expand the panel.
  const aiInputRef = useRef(null);
  const tasksSidebar = useTasksSidebar(aiInputRef);
  const fileInputRef = useRef(null);
  // Imperative handle to the resizable panel wrapping AiModelInput, so
  // `showAiModelInput` collapses/expands it (react-resizable-panels) instead
  // of unmounting <AiModelInput>, which would otherwise wipe out everything
  // it's holding (selected model, in-progress prompt, artifact being edited).
  const aiPanelRef = useRef(null);

  const initialStatusResolved = initialStatus || {};
  const [selectedFilterTags, setSelectedFilterTags] = useState(() =>
    Array.isArray(initialCategories)
      ? initialCategories
      : show.asset_categories.map((tag) => tag._id),
  );
  const [showUntagged, setShowUntagged] = useState(true);
  const [showDraft, setShowDraft] = useState(
    initialStatusResolved.draft !== false,
  );
  const [showApproved, setShowApproved] = useState(
    initialStatusResolved.approved !== false,
  );
  const [showRejected, setShowRejected] = useState(
    initialStatusResolved.rejected !== false,
  );

  const [showAiModelInput, setShowAiModelInput] = useState(true);
  // Hydrated from the caller (URL/props) so refresh, deep-link and back/forward
  // preserve the archived view, matching the other filters. Accepts a real
  // boolean or the string "true" that survives a round-trip through the URL.
  const [showArchived, setShowArchived] = useState(
    initialArchived === true || initialArchived === "true",
  );

  // Collapse/expand rather than unmount — see aiPanelRef above. Only depends
  // on showAiModelInput: when showArchived toggles, the panel itself still
  // unmounts/remounts (no AI generation in the archived view), and its
  // `defaultSize` prop (keyed off showAiModelInput) already gets a freshly
  // (re)mounted panel to the right size — calling the imperative API in that
  // same tick races the panel group's registration of the new panel's
  // constraints and throws ("Panel constraints not found").
  useEffect(() => {
    const panel = aiPanelRef.current;
    if (!panel) return;
    if (showAiModelInput) panel.expand();
    else panel.collapse();
  }, [showAiModelInput]);
  // Media-type filter (intersected with the prop-allowed prefixes when querying).
  // Defaults to all prefixes the editor is allowed to show, or the caller's
  // initial selection (e.g. restored from the URL) when provided.
  const [selectedMediaTypes, setSelectedMediaTypes] = useState(() => {
    const allowed = toMediaPrefixes(mediaTypes);
    if (Array.isArray(initialMedia)) {
      const picked = initialMedia.filter((p) => allowed.includes(p));
      return picked.length > 0 ? picked : allowed;
    }
    return allowed;
  });
  const [searchString, setSearchString] = useState("");
  const [searching, setSearching] = useState(false);

  const [selectedCharacterIds, setSelectedCharacterIds] = useState(() => {
    const known = new Set((show.characters || []).map((c) => c._id));
    return String(initialCharacterFilter || "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => known.has(id));
  });

  const [sort, setSort] = useState(() =>
    ASSET_SORT_DROPDOWN_OPTIONS.some((o) => o.value === initialSort)
      ? initialSort
      : DEFAULT_ASSET_SORT,
  );

  const safeInitialPage = Math.max(1, parseInt(initialPage, 10) || 1);
  const [page, setPage] = useState(safeInitialPage);
  const currentPageRef = useRef(safeInitialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssets, setTotalAssets] = useState(0);

  const goToPage = (nextPage) => {
    currentPageRef.current = nextPage;
    setPage(nextPage);
  };

  const [sketchEditorState, setSketchEditorState] = useState(null);
  const [editingAssetId, setEditingAssetId] = useState(null);

  const [selectMode, setSelectMode] = useState(initialSelectMode);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  // The asset most recently clicked/previewed (normal mode). It keeps the
  // yellow selection outline after the preview closes so the user can see
  // which asset they just looked at, until they click another asset, a
  // folder, or empty space.
  const [previewedAssetId, setPreviewedAssetId] = useState(null);
  // User-controlled column count for the asset grid (4..8, default 4).
  const [gridCols, setGridCols] = useState(() => clampZoom(initialZoom));
  // When ON, search is scoped to the current folder. When OFF, search runs
  // globally across all folders the user can see.
  const [searchInFolder, setSearchInFolder] = useState(false);
  // Tracks the in-flight assets request so we can abort it when filters
  // change quickly (e.g. typing in the search box).
  const fetchAssetsAbortRef = useRef(null);
  // The scrollable grid container. We reset it to the top whenever the user
  // pages so each new page starts at the beginning of the list rather than
  // keeping the previous page's scroll offset.
  const scrollContainerRef = useRef(null);
  // When set, the next fetch triggered by a folder/filter change targets this
  // page instead of resetting to page 1. Used by "Go to folder" so we land on
  // the page that actually contains the asset.
  const pendingPageRef = useRef(null);
  // Folder ids that are currently being dragged. Used to grey out the source
  // card and prevent dropping a folder onto itself.
  const [draggingFolderIds, setDraggingFolderIds] = useState([]);

  const beginFolderDrag = (ids) => {
    if (!ids || ids.length === 0) return;
    setDraggingFolderIds(ids);
    const cleanup = () => {
      setDraggingFolderIds([]);
      window.removeEventListener("dragend", cleanup, true);
      window.removeEventListener("drop", cleanup, true);
    };
    window.addEventListener("dragend", cleanup, true);
    window.addEventListener("drop", cleanup, true);
  };

  // True while an internal asset/folder drag is in progress. Drives the
  // "drop to delete" trash zone, which is only mounted during a drag.
  const [isDraggingItems, setIsDraggingItems] = useState(false);
  // Highlights the trash zone while the dragged payload hovers over it.
  const [trashDragOver, setTrashDragOver] = useState(false);

  // Called by AssetItem / FolderCard when an internal drag starts. We mount
  // the trash drop zone and tear it down on `dragend`, which fires on the
  // drag source after the drop completes (covering both successful drops and
  // cancels). We deliberately do NOT clean up on a capture-phase `drop`
  // listener: that fires before the trash zone's own onDrop and would unmount
  // the zone (React flushes the discrete drop update synchronously) before the
  // drop is ever handled. handleTrashDrop clears the state itself on success.
  const beginItemDrag = ({ assetIds = [], folderIds = [] } = {}) => {
    if (assetIds.length === 0 && folderIds.length === 0) return;
    setIsDraggingItems(true);
    const cleanup = () => {
      setIsDraggingItems(false);
      setTrashDragOver(false);
      window.removeEventListener("dragend", cleanup, true);
    };
    window.addEventListener("dragend", cleanup, true);
  };
  // Ids of assets/folders currently in flight for move/visibility ops, used to
  // surface a loader on the affected card(s).
  const [movingAssetIds, setMovingAssetIds] = useState([]);
  const [loadingFolderIds, setLoadingFolderIds] = useState([]);

  const addMovingAssets = (ids) =>
    setMovingAssetIds((prev) => Array.from(new Set([...prev, ...ids])));
  const removeMovingAssets = (ids) =>
    setMovingAssetIds((prev) => prev.filter((id) => !ids.includes(id)));
  const addLoadingFolder = (id) =>
    setLoadingFolderIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const removeLoadingFolder = (id) =>
    setLoadingFolderIds((prev) => prev.filter((f) => f !== id));

  // Folder is initialized from the `folder` prop and managed internally;
  // changes are reported to the parent via `onFolderChange`.
  const [currentFolderId, setCurrentFolderIdInternal] = useState(
    folderProp ?? null,
  );
  const isSearching = !!searchString;

  // If the parent updates the `folder` prop, sync internal state without
  // re-firing the change callback.
  useEffect(() => {
    const next = folderProp ?? null;
    if (next !== currentFolderId) setCurrentFolderIdInternal(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderProp]);

  const setCurrentFolderId = (folderId) => {
    const next = folderId ?? null;
    setCurrentFolderIdInternal(next);
    onFolderChange?.(next);
  };

  // Pre-compute media-type filter once per render. The query intersects the
  // user-selected types with the prop-allowed types so callers can lock down
  // the editor to e.g. images-only and the user filter is constrained to that.
  const allowedMediaPrefixes = toMediaPrefixes(mediaTypes);
  const characterFilterQuery = selectedCharacterIds.join(",");
  const mediaTypeQuery = selectedMediaTypes
    .filter((t) => allowedMediaPrefixes.includes(t))
    .join(",");

  // Single unified fetch: server returns folders + assets for the current page,
  // sequentially paginated (folders first, then assets) so neither repeats
  // across pages. Also returns ancestors for breadcrumb.
  const fetchItems = async (pageIndex) => {
    // Cancel any previous in-flight request so we don't get racing responses
    // when the user types quickly or rapidly changes filters.
    if (fetchAssetsAbortRef.current) {
      fetchAssetsAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAssetsAbortRef.current = controller;

    try {
      const params = new URLSearchParams();
      params.set("page", String(pageIndex || page));
      params.set("search", searchString);
      params.set("tags", selectedFilterTags.map((tag) => tag).join(","));
      params.set("showUntagged", String(showUntagged));
      params.set("showDraft", String(showDraft));
      params.set("showApproved", String(showApproved));
      params.set("showRejected", String(showRejected));
      params.set("archived", String(showArchived));
      params.set("size", String(pageSize));
      if (mediaTypeQuery) params.set("mediaType", mediaTypeQuery);
      params.set("sort", sort);
      if (characterFilterQuery) params.set("characters", characterFilterQuery);
      if (!isSearching || searchInFolder) {
        // Folder scoping:
        // - Not searching: always scope to the current folder (root if null).
        // - Searching + "Folder" toggle ON: scope search to the current folder.
        // - Searching + "Folder" toggle OFF: search globally (omit folder param,
        //   the backend then searches across all visible folders and excludes
        //   folders from the results).
        params.set("folder", currentFolderId || "");
      }
      const response = await apiFetch(
        `/api/shows/${show._id}/assets/items?${params.toString()}`,
        { signal: controller.signal },
      );
      if (!response.ok) throw new Error("Failed to fetch items");
      const data = await response.json();

      // Out-of-range page guard: if the requested page is past the end,
      // fall back to the last page (or page 1 when empty).
      const requested = pageIndex || page;
      if (data.totalPages > 0 && requested > data.totalPages) {
        if (fetchAssetsAbortRef.current === controller) {
          fetchAssetsAbortRef.current = null;
        }
        return await fetchItems(data.totalPages);
      }

      goToPage(data.page);
      setTotalPages(data.totalPages);
      setTotalAssets(data.total);
      setPageSize(data.pageSize);
      setFolders(data.folders || []);
      setAssets(data.assets || []);
      releaseLocksForItemsNoLongerOnGrid(
        new Set([
          ...(data.folders || []).map((folder) => folder._id),
          ...(data.assets || []).map((asset) => asset._id),
        ]),
      );
      setTasks(data.tasks || []);
      setAncestors(data.ancestors || []);
      return data;
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error fetching items:", error);
      setLoading(false);
    } finally {
      if (fetchAssetsAbortRef.current === controller) {
        fetchAssetsAbortRef.current = null;
      }
    }
  };

  // Asks the backend which folder + page an asset sits on under the *current*
  // filters (folders sort first, then assets, so the page depends on
  // tags/status/media and the page size). Returns null when the lookup fails.
  //
  // @input '671f8a…'
  // @output {folder: '66ab…', page: 4}
  const locateAsset = async (assetId) => {
    try {
      const params = new URLSearchParams();
      params.set("tags", selectedFilterTags.map((tag) => tag).join(","));
      params.set("showUntagged", String(showUntagged));
      params.set("showDraft", String(showDraft));
      params.set("showApproved", String(showApproved));
      params.set("showRejected", String(showRejected));
      params.set("archived", String(showArchived));
      params.set("size", String(pageSize));
      if (mediaTypeQuery) params.set("mediaType", mediaTypeQuery);
      params.set("sort", sort);
      if (characterFilterQuery) params.set("characters", characterFilterQuery);

      const response = await apiFetch(
        `/api/shows/${show._id}/assets/${assetId}/locate?${params.toString()}`,
      );
      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      console.error("Error locating asset:", error);
      return null;
    }
  };

  const {
    refreshCurrentPage,
    refreshToFirstPage,
    refreshCurrentPageIfFirstPage,
    refreshUnlessLocked,
    handleItemEditingChange,
    releaseLocksForItemsNoLongerOnGrid,
  } = useHandleRefresh({
    fetchItems,
    getCurrentPage: () => currentPageRef.current,
  });

  const refreshAsset = async (assetId) => {
    try {
      const res = await apiFetch(`/api/shows/${show._id}/assets/${assetId}`);
      if (!res.ok) throw new Error("Failed to fetch asset");
      const fresh = await res.json();
      setAssets((prevAssets) =>
        prevAssets.map((a) => (a._id === assetId ? fresh : a)),
      );
      return fresh;
    } catch (error) {
      console.error("Failed to refresh asset", error);
      return null;
    }
  };

  // When a `generate_assets` task completes for the folder the user is
  // currently viewing, silently refetch so the new asset shows up without
  // requiring a toast click. The matching toast is suppressed in
  // WebSocketContext so the user only sees one outcome at a time.
  useWebSocket(["user_notification"], {
    onMessage: (message) => {
      const data = message?.data;
      if (!data || data.generate_assets !== true) return;
      const taskFolder = data.destination_folder || null;
      if (taskFolder !== (currentFolderId || null)) return;
      refreshUnlessLocked();
    },
  });

  // Refetch when a task changes state so in-progress generation cards appear
  // (and clear) without a manual refresh. A just-submitted task is `pending`
  // until a worker picks it up and flips it to `in_progress` — which is the
  // moment its card should show — so the submit-time refresh alone isn't
  // enough. The task_updated payload only carries { taskId, state, status }
  // (no folder), so we refetch on any task event and let the backend scope to
  // the current folder.
  useWebSocket(["task_updated", "task_completed", "task_failed"], {
    onMessage: () => {
      refreshUnlessLocked();
    },
  });

  // MCP tools that create/move/rename/copy a folder or asset (create_folder,
  // update_folder, update_asset, copy_asset) push this so the change shows up
  // without a manual refresh. Content generation doesn't fire it — a running
  // task already drives its own refetch via the task_updated listener above.
  useWebSocket(["mcp_assets_changed"], {
    onMessage: () => {
      refreshUnlessLocked();
    },
  });

  // The generate_prompt MCP tool pushes a composed prompt for the user to
  // review/iterate on before generating. Only apply it if this is the Assets
  // page the user actually has open — same check WebSocketContext uses to
  // suppress the matching toast — so it doesn't silently land in a
  // background tab, a character-assets page, or a modal/embedded editor.
  useWebSocket(["mcp_smart_prompt_updated"], {
    onMessage: (message) => {
      const smartPrompt = message?.data?.smartPrompt;
      if (typeof smartPrompt !== "string") return;
      if (!window.location.pathname.endsWith("/show/assets")) return;
      setShowAiModelInput(true);
      aiInputRef.current?.recallSettings({ smart: true, smartPrompt });
    },
  });

  // Whether we've completed the very first fetch yet. The first fetch should
  // honor the `initialPage` prop instead of resetting to page 1, so that
  // callers can deep-link to a specific page.
  const didInitialFetch = useRef(false);

  useEffect(() => {
    const getData = async () => {
      let startPage;
      if (pendingPageRef.current != null) {
        // Explicit page requested (e.g. "Go to folder").
        startPage = pendingPageRef.current;
        pendingPageRef.current = null;
      } else {
        // Filter/folder changes otherwise reset to the first page.
        startPage = didInitialFetch.current ? 1 : safeInitialPage;
      }
      goToPage(startPage);
      setSearching(true);
      await fetchItems(startPage);
      didInitialFetch.current = true;
      setSearching(false);
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchString,
    selectedFilterTags,
    showUntagged,
    showDraft,
    showApproved,
    showRejected,
    showArchived,
    currentFolderId,
    searchInFolder,
    pageSize,
    mediaTypeQuery,
    sort,
    characterFilterQuery,
  ]);

  // Report selection changes upward.
  useEffect(() => {
    if (!onSelectedItemsChange) return;
    onSelectedItemsChange({
      folderIds: selectedFolderIds,
      assetIds: selectedAssetIds,
      folders: folders.filter((f) => selectedFolderIds.includes(f._id)),
      assets: assets.filter((a) => selectedAssetIds.includes(a._id)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderIds, selectedAssetIds]);

  // Report setting changes upward (used by parents to persist preferences).
  // The first render also fires these so parents see the initial values.
  useEffect(() => {
    onCategoriesChange?.(selectedFilterTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterTags]);

  useEffect(() => {
    onStatusChange?.({
      approved: showApproved,
      draft: showDraft,
      rejected: showRejected,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showApproved, showDraft, showRejected]);

  useEffect(() => {
    onMediaChange?.(selectedMediaTypes, allMediaSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMediaTypes]);

  useEffect(() => {
    onArchivedChange?.(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    onSortChange?.(sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => {
    onCharacterFilterChange?.(characterFilterQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterFilterQuery]);

  useEffect(() => {
    onPageChange?.(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    onZoomChange?.(gridCols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridCols]);

  // Apply an externally-requested generation preset (model + payload props)
  // to the floating generator. Keyed on `nonce` so repeated requests with the
  // same model/settings still re-open and re-fill the panel.
  useEffect(() => {
    if (!presetGeneration) return;
    setShowAiModelInput(true);
    aiInputRef.current?.recallSettings({
      modelId: presetGeneration.modelId,
      settings: presetGeneration.settings,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetGeneration?.nonce]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const navigateToPage = async (targetPage) => {
    setPreviewedAssetId(null);
    goToPage(targetPage);
    setLoading(true);
    await fetchItems(targetPage);
    setLoading(false);
    scrollToTop();
  };

  const handleFirstPage = async () => {
    if (page <= 1) return;
    await navigateToPage(1);
  };

  const handlePreviousPage = async () => {
    if (page <= 1) return;
    await navigateToPage(page - 1);
  };

  const handleNextPage = async () => {
    if (page >= totalPages) return;
    await navigateToPage(page + 1);
  };

  // Loads the page adjacent to the current one (`direction` is +1 / -1, or 0 to
  // reload the page we are on after an archive/restore) and returns the raw
  // fetch payload (assets + pagination). Used by the asset
  // preview so it can page across boundaries while navigating. We delegate
  // through a ref so the callback handed to the (snapshot) modal always runs
  // against the latest `page`/`totalPages`/filters instead of a stale closure.
  const requestPreviewPageImpl = async ({ direction, fromPage }) => {
    // `fromPage` is the preview's own idea of where it is. A websocket
    // refresh() resets the parent to page 1 without the modal knowing, so
    // resolving against the parent's live `page` would silently retarget the
    // modal's request at the wrong page.
    const target = (fromPage ?? page) + direction;
    if (target < 1 || target > totalPages) return null;
    setLoading(true);
    try {
      const data = await fetchItems(target);
      // Keep the underlying grid aligned to the top so that when the user
      // closes the preview the new page starts at the beginning, matching
      // the normal (preview-off) paging behavior.
      scrollToTop();
      return data;
    } finally {
      setLoading(false);
    }
  };
  const requestPreviewPageRef = useRef(requestPreviewPageImpl);
  requestPreviewPageRef.current = requestPreviewPageImpl;
  const requestPreviewPage = useCallback(
    (options) => requestPreviewPageRef.current(options),
    [],
  );

  // Archives a single asset behind a confirm dialog. Resolves `true` only when
  // the asset really moved to the archive, so a caller can tell a completed
  // archive apart from a cancelled confirm. Refetching is deliberately left to
  // the caller: the grid jumps back to page 1, while the preview reloads the
  // page it is sitting on so it can advance to the next asset.
  const archiveAsset = async ({ asset, awaitCompletion = true }) => {
    const confirmed = await openModal(
      <ConfirmModal
        title="Archive Asset"
        message={`Archive '${asset.name}'? It can be restored later from the archived view.`}
      />,
    );
    if (!confirmed) return false;

    const archiveRequest = apiFetch(
      `/api/shows/${show._id}/assets/${asset._id}`,
      {
        method: "PUT",
        body: JSON.stringify({ archived: true }),
      },
    )
      .then((assetResponse) => {
        if (!assetResponse.ok) throw new Error("Failed to archive asset");
        return true;
      })
      .catch((error) => {
        console.error("Error in archive:", error);
        toast.error(`Could not archive '${asset.name}'`);
        return false;
      });

    if (awaitCompletion) return archiveRequest;

    return true;
  };

  const handleDelete = async (asset) => {
    const archived = await archiveAsset({ asset });
    if (!archived) return;

    // `refreshCurrentPage()`, not `refresh()` — refresh() is fetchItems(1),
    // which throws the user back to page 1 of the grid after archiving from
    // page 3. Losing your place is the complaint this ticket exists to fix,
    // and it is the same one whether you archive from the grid or from the
    // preview. The hook resolves the page from a ref rather than the
    // render-time `page`, so a websocket refresh can't retarget it midway.
    await refreshCurrentPage();
  };

  const handleStatusChange = async (asset, status) => {
    try {
      const response = await apiFetch(
        `/api/shows/${show._id}/assets/${asset._id}`,
        {
          method: "PUT",
          body: JSON.stringify({ status }),
        },
      );
      if (!response.ok) throw new Error("Failed to update asset");
      await refreshCurrentPage();
    } catch (error) {
      console.error("Error in status change:", error);
    }
  };

  const handleDownload = async (asset) => {
    try {
      await downloadFile(asset.url, asset.name);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleEditAsset = async (asset) => {
    const assetData = await openModal(
      <AssetEditorModal asset={asset} accept={mediaTypes} />,
      "side",
    );

    if (!assetData) return;

    try {
      if (!asset) setLoading(true);
      let uploadResponse = {};

      if (assetData.file) {
        setLoadingStatus(`Uploading: ${assetData.file.name}`);
        uploadResponse = await uploadFile(assetData.file);
        assetData.mediaType = assetData.file.type.split("/")[0];
        delete assetData.file;
      }

      setLoadingStatus(`Saving: ${assetData.name}`);
      const assetResponse = await apiFetch(
        `/api/shows/${show._id}/assets/${asset?._id || ""}`,
        {
          method: asset?._id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...assetData,
            ...uploadResponse,
            source: "upload",
            ...(asset?._id ? {} : { folder: currentFolderId }),
          }),
        },
      );

      if (!assetResponse.ok) throw new Error("Failed to add asset");

      await refreshToFirstPage();
    } catch (error) {
      console.error("Error in file select:", error);
    } finally {
      setLoading(false);
      setLoadingStatus("");
    }
  };

  const handleComment = async (asset) => {
    const comment = await openModal(
      <CommentsEditorModal uid={asset._id} />,
      "side",
    );
    if (comment) {
      await refreshCurrentPage();
    }
  };

  // The preview stays open after an archive/restore and advances to the next
  // asset, so it owns the refetch (through `onRequestPage`) and is handed the
  // mutation-only primitives rather than the grid handlers, which refresh back
  // to page 1 and would pull the list out from under it.
  const handleAssetsPreview = (asset) => {
    setPreviewedAssetId(asset._id);
    openModal(
      <AssetPreviewModal
        asset={asset}
        assets={assets}
        page={page}
        totalPages={totalPages}
        onRequestPage={requestPreviewPage}
        onNavigate={(a) => setPreviewedAssetId(a._id)}
        onDelete={(asset) => archiveAsset({ asset, awaitCompletion: false })}
        onCopy={handleCopyAsset}
        onMove={handleMoveAsset}
        onSketchEdit={handleOpenSketchEditor}
        onScene3DEdit={handleOpenScene3DEditor}
        onCaptured={refreshCurrentPage}
        onRenamed={refreshCurrentPage}
        refreshAsset={refreshAsset}
        onRestoreFromArchive={(assetIds) =>
          restoreAssetsFromArchive({ assetIds, notifyOnSuccess: false })
        }
        onDestroy={handleDestroy}
        onAddToList={addAssetToList}
      />,
    );
  };

  // Preset the floating AiModelInput from the asset's recorded generation
  // (model + payload settings) and expand it if minimized. The `generation`
  // sub-document is populated by the generation pipeline (see Asset schema);
  // `model` may be either a populated object or a raw ObjectId string.
  const handleAddAssetToList = (asset) => addAssetToList({ asset });

  const handleRecallSettings = (asset) => {
    const gen = asset?.generation;
    if (!gen) return;
    const modelId = gen.model?._id || gen.model;
    if (!modelId || !gen.settings) return;
    setShowAiModelInput(true);
    aiInputRef.current?.recallSettings({
      modelId,
      settings: gen.settings,
      smart: gen.smart,
      smartPrompt: gen.smartPrompt,
    });
  };

  const handleCompleteDraft = async (asset) => {
    const draftId = asset?.generation?.metadata?.draft_id;
    if (!draftId || !show?._id) return;
    try {
      await runTask({
        modelId: BYTEDANCE_DRAFT_MODEL_ID,
        payload: { draft_id: draftId },
        showId: show._id,
        generate_assets: true,
        destination_folder: currentFolderId || null,
        name: asset?.name ? `${asset.name}_Complete` : undefined,
      });
      refreshCurrentPageIfFirstPage();
      tasksSidebar.refresh();
    } catch (err) {
      console.error("Failed to complete draft:", err);
      toast.error("Failed to complete draft");
    }
  };

  const handleAgent = async (asset) => {
    try {
      const res = await apiFetch(`/api/shows/${show._id}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "New Chat",
          assets: [
            { url: asset.url, type: asset.mediaType, name: asset.name || "" },
          ],
        }),
      });
      if (!res.ok) throw new Error("Failed to create conversation");
      navigate("/show/agent");
    } catch (err) {
      console.error("Failed to create agent conversation:", err);
      toast.error("Failed to start agent conversation");
    }
  };

  const handleOpenSketchEditor = (asset) => {
    setEditingAssetId(asset?._id || null);
    if (asset?.sketch) {
      setSketchEditorState({ sketchId: asset.sketch });
    } else {
      setSketchEditorState({
        sketchId: null,
        initialImageUrl: asset?.url || null,
        initialName: asset?.name || null,
      });
    }
  };

  const handleCreateSketch = () => {
    setEditingAssetId(null);
    setSketchEditorState({ sketchId: null });
  };

  const handleCloseSketchEditor = () => {
    setSketchEditorState(null);
    setEditingAssetId(null);
  };

  const handleSketchSaveComplete = async ({
    sketchId,
    imageUrl,
    imageKey,
    name,
  }) => {
    try {
      if (editingAssetId) {
        await apiFetch(`/api/shows/${show._id}/assets/${editingAssetId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            url: imageUrl,
            key: imageKey,
            sketch: sketchId,
            thumbnail_url: imageUrl,
            thumbnail_key: imageKey,
          }),
        });
      } else {
        await apiFetch(`/api/shows/${show._id}/assets`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mediaType: "image",
            name,
            url: imageUrl,
            key: imageKey,
            thumbnail_url: imageUrl,
            thumbnail_key: imageKey,
            source: "sketch",
            sketch: sketchId,
            folder: currentFolderId,
          }),
        });
      }
      toast.success("Asset saved");
      handleCloseSketchEditor();
      await refreshToFirstPage();
    } catch (error) {
      console.error("Error saving sketch asset:", error);
      toast.error("Failed to save asset");
    }
  };

  // The 3D editor lives on its own route (Scene3DPage) rather than an
  // in-place overlay — navigating there carries whatever this asset/folder
  // needs for Scene3DPage's own save-complete handler (the equivalent of
  // this file's old handleScene3DSaveComplete) to upsert the right Asset
  // doc, since that page can't reach into this component's state.
  const buildScene3DPath = (sceneId, assetId) => {
    const params = new URLSearchParams();
    if (currentFolderId) params.set("folder", currentFolderId);
    if (assetId) params.set("assetId", assetId);
    const qs = params.toString();
    return `/show/scene3d/${sceneId || "new"}${qs ? `?${qs}` : ""}`;
  };

  // A 3D scene can't be reconstructed from an arbitrary existing asset the
  // way an image can be dropped into the sketch editor as a background —
  // opening the editor for editing only ever happens via the explicit
  // "Edit 3D Scene" menu item (gated on `asset.scene3d`), never from a plain
  // image/asset click.
  const handleOpenScene3DEditor = (asset) => {
    navigate(buildScene3DPath(asset?.scene3d, asset?._id));
  };

  const handleCreateScene3D = () => {
    navigate(buildScene3DPath(null, null));
  };

  // Only react to external file drags (uploads) — internal asset drags should
  // not change the editor background.
  const isFileDrag = (e) => {
    const types = e.dataTransfer?.types;
    return !!(types && Array.from(types).includes("Files"));
  };

  const handleDragEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();

    // `relatedTarget` is whatever the pointer moved onto. Staying inside the drop
    // zone means we crossed between grid children and the overlay must stay; moving
    // onto the generator panel or the resize handle — siblings outside this
    // container — leaves it, and so does leaving the window, where
    // `relatedTarget` is null.
    //
    // The `Node` check is not ceremony: `relatedTarget` is typed `EventTarget | null`,
    // and `contains()` throws a TypeError on an EventTarget that isn't a Node, which
    // would escape this handler mid-drag and strand the overlay. null and undefined
    // are safe to pass, but a non-Node is not.
    const movedOnto = e.relatedTarget;

    if (movedOnto instanceof Node && e.currentTarget.contains(movedOnto))
      return;

    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const uploadFiles = async (fileList) => {
    const allFiles = Array.from(fileList || []);
    const files = allFiles.filter((file) => {
      const prefix = file.type.split("/")[0];
      return (
        prefix &&
        (allowedMediaPrefixes.length === 0 ||
          allowedMediaPrefixes.includes(prefix))
      );
    });
    const skipped = allFiles.length - files.length;
    if (skipped > 0) {
      toast.warning(
        `Skipped ${skipped} file${skipped === 1 ? "" : "s"} with an unsupported type`,
      );
    }
    if (files.length === 0) return;

    setLoading(true);
    for (const [index, file] of files.entries()) {
      const fileType = file.type.split("/")[0];
      try {
        setLoadingStatus(
          `Uploading: ${file.name} (${index + 1}/${files.length})`,
        );
        const uploadResponse = await uploadFile(file);
        const fileName = file.name.replace(/\.[^/.]+$/, "");

        setLoadingStatus(`Saving: ${fileName} (${index + 1}/${files.length})`);

        const assetResponse = await apiFetch(`/api/shows/${show._id}/assets`, {
          method: "POST",
          body: JSON.stringify({
            ...uploadResponse,
            mediaType: fileType,
            name: fileName,
            source: "upload",
            folder: currentFolderId,
          }),
        });

        if (!assetResponse.ok) throw new Error("Failed to add asset");
      } catch (error) {
        console.error("Error in file upload:", error);
      }
    }

    await refreshToFirstPage();
    setLoading(false);
    setLoadingStatus("");
  };

  useEffect(() => {
    const clearDragOverlay = () => setIsDragOver(false);

    window.addEventListener("drop", clearDragOverlay, true);
    window.addEventListener("dragend", clearDragOverlay, true);

    return () => {
      window.removeEventListener("drop", clearDragOverlay, true);
      window.removeEventListener("dragend", clearDragOverlay, true);
    };
  }, []);

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await uploadFiles(e.dataTransfer.files);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      await uploadFiles(files);
    }

    e.target.value = "";
  };

  const handleFilterTagChange = (tagId, checked) => {
    setSelectedFilterTags(
      checked
        ? [...selectedFilterTags, tagId]
        : selectedFilterTags.filter((t) => t !== tagId),
    );
  };

  const tagAssignmentTargetAssets =
    selectedAssetIds.length > 0
      ? assets.filter((asset) => selectedAssetIds.includes(asset._id))
      : assets;

  const handleAssignTags = () => {
    if (selectedAssetIds.length === 0) return;
    const targets = assets.filter((asset) =>
      selectedAssetIds.includes(asset._id),
    );
    openModal(
      <TagsModal
        assetCategories={show.asset_categories || []}
        assets={targets}
        onSaved={refreshCurrentPage}
      />,
    );
  };

  const handleAssignCharacters = () => {
    if (selectedAssetIds.length === 0) return;
    const targets = assets.filter((asset) =>
      selectedAssetIds.includes(asset._id),
    );
    openModal(
      <CharacterAssetsModal
        characters={show.characters || []}
        assets={targets}
        onSaved={refreshCurrentPage}
      />,
    );
  };

  const handleBulkRename = async () => {
    if (selectedAssetIds.length === 0) {
      return;
    }

    const assetIds = [...selectedAssetIds];

    const result = await openModal(<BulkRenameModal assetIds={assetIds} />);

    if (!result) {
      return;
    }

    try {
      await commitBulkRename({ assetIds, ...result });
      setSelectedAssetIds([]);
      await refreshCurrentPage();
      toast.success(
        `Renamed ${assetIds.length} asset${assetIds.length === 1 ? "" : "s"}`,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleBulkRenameFolder = async () => {
    if (!currentFolderId) {
      return;
    }

    let assetIds = [];
    try {
      const params = new URLSearchParams();

      params.set("folder", currentFolderId || ""); // present + empty = root
      params.set("sort", sort);
      params.set("search", searchString);
      params.set("tags", selectedFilterTags.map((tag) => tag).join(","));
      params.set("showUntagged", String(showUntagged));
      params.set("showDraft", String(showDraft));
      params.set("showApproved", String(showApproved));
      params.set("showRejected", String(showRejected));
      params.set("archived", String(showArchived));

      if (mediaTypeQuery) {
        params.set("mediaType", mediaTypeQuery);
      }

      if (characterFilterQuery) {
        params.set("characters", characterFilterQuery);
      }

      const res = await apiFetch(
        `/api/shows/${show._id}/assets/ids?${params.toString()}`,
      );

      if (!res.ok) {
        throw new Error("Failed to fetch folder assets");
      }

      assetIds = (await res.json()).assetIds || [];
    } catch (error) {
      console.error(error);
      toast.error("Could not load folder assets");
      return;
    }

    if (assetIds.length === 0) {
      toast.info("No assets in this folder to rename");
      return;
    }

    const result = await openModal(<BulkRenameModal assetIds={assetIds} />);

    if (!result) {
      return;
    }

    try {
      await commitBulkRename({ assetIds, ...result });
      await refreshCurrentPage();
      toast.success(
        `Renamed ${assetIds.length} asset${assetIds.length === 1 ? "" : "s"}`,
      );
    } catch (error) {
      console.error(error);
    }
  };

  // ---- Bulk download ------------------------------------------------------
  // Assets are zipped client-side in a background Web Worker (see
  // `useAssetsDownload`), so nothing is buffered server-side and progress shows
  // up as a toast. The last breadcrumb ancestor is the folder we're currently
  // in — used to name the archive.

  const currentFolderName = ancestors.length
    ? ancestors[ancestors.length - 1].name
    : null;

  const handleDownloadSelection = () => {
    if (selectedAssetIds.length === 0) return;
    const scopeName = currentFolderName || show.name || "assets";
    downloadAssets([...selectedAssetIds], `${scopeName} - selection`);
  };

  const handleDownloadCurrentFolder = () => {
    if (!currentFolderId) return;
    downloadFolder(currentFolderId, currentFolderName || "assets");
  };

  const handleDownloadFolder = (folder) => {
    if (!folder?._id) return;
    downloadFolder(folder._id, folder.name || "assets");
  };

  // True when every category is selected and untagged is included — i.e. no
  // tag filtering is in effect. Used to hide the count badge (same behavior
  // as the Media dropdown).
  const allTagsSelected =
    showUntagged &&
    show.asset_categories.every((tag) => selectedFilterTags.includes(tag._id));

  // True when no status filtering is in effect (all three statuses shown).
  const allStatusSelected = showDraft && showApproved && showRejected;
  const selectedStatusCount =
    (showDraft ? 1 : 0) + (showApproved ? 1 : 0) + (showRejected ? 1 : 0);

  // True when no media-type filtering is in effect (all allowed types shown).
  const selectedMediaCount = selectedMediaTypes.filter((t) =>
    allowedMediaPrefixes.includes(t),
  ).length;
  const allMediaSelected = selectedMediaCount === allowedMediaPrefixes.length;

  // Shared button styles for the unified select-mode action group (mirrors the
  // onion-skin control grouping in the SketchEditor toolbar).
  const selectActionBtn =
    "flex items-center justify-center w-7 h-7 rounded-md text-secondary-foreground hover:bg-secondary cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent";
  const selectDangerBtn =
    "flex items-center justify-center w-7 h-7 rounded-md text-secondary-foreground hover:bg-destructive hover:text-destructive-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-secondary-foreground";
  const selectRestoreBtn =
    "flex items-center justify-center w-7 h-7 rounded-md text-secondary-foreground hover:bg-success hover:text-success-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-secondary-foreground";

  // ---- Folder operations ---------------------------------------------------

  const handleCreateFolder = async () => {
    const result = await openModal(
      <FolderEditorModal title="New Folder" />,
      "default",
    );
    if (!result) return;
    try {
      const res = await apiFetch(`/api/shows/${show._id}/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: result.name,
          visibility: result.visibility,
          parent: currentFolderId,
          type: "assets",
        }),
      });
      if (!res.ok) throw new Error("Failed to create folder");
      await refreshToFirstPage();
      toast.success("Folder created");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create folder");
    }
  };

  const handleRenameFolder = async (folder) => {
    const result = await openModal(
      <FolderEditorModal folder={folder} title="Edit Folder" />,
      "default",
    );
    if (!result) return;
    try {
      const res = await apiFetch(
        `/api/shows/${show._id}/folders/${folder._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: result.name,
            visibility: result.visibility,
          }),
        },
      );
      if (!res.ok) throw new Error("Failed to update folder");
      await refreshCurrentPage();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update folder");
    }
  };

  const handleInlineRenameAsset = async (asset, name) => {
    try {
      const res = await apiFetch(`/api/shows/${show._id}/assets/${asset._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error("Failed to rename asset");
      await refreshCurrentPage();
    } catch (error) {
      console.error(error);
      toast.error("Failed to rename asset");
    }
  };

  const handleInlineRenameFolder = async (folder, name) => {
    try {
      const res = await apiFetch(
        `/api/shows/${show._id}/folders/${folder._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        },
      );
      if (!res.ok) throw new Error("Failed to rename folder");
      await refreshCurrentPage();
    } catch (error) {
      console.error(error);
      toast.error("Failed to rename folder");
    }
  };

  const handleToggleFolderVisibility = async (folder) => {
    const next = folder.visibility === "private" ? "public" : "private";
    addLoadingFolder(folder._id);
    try {
      const res = await apiFetch(
        `/api/shows/${show._id}/folders/${folder._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visibility: next }),
        },
      );
      if (!res.ok) throw new Error("Failed to update folder");
      await refreshCurrentPage();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update folder");
    } finally {
      removeLoadingFolder(folder._id);
    }
  };

  const handleDeleteFolder = async (folder) => {
    const confirm = await openModal(
      <ConfirmModal
        title="Delete Folder"
        type="destructive"
        message={`Deleting "${folder.name}" also deletes every sub-folder it contains, and archives every asset inside them. The folders cannot be recovered. Continue?`}
      />,
    );
    if (!confirm) return;
    try {
      const res = await apiFetch(
        `/api/shows/${show._id}/folders/${folder._id}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error("Failed to delete folder");
      await refreshCurrentPage();
      toast.success("Folder deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete folder");
    }
  };

  const handleOpenFolder = (folder) => {
    clearSelection();
    setCurrentFolderId(folder._id);
  };

  const handleNavigateToFolder = (folderId) => {
    clearSelection();
    setCurrentFolderId(folderId);
  };

  // Used by search-result "Go to folder" button on asset cards. Asks the
  // backend which folder + page the asset lives on under the *current* filters
  // (folders sort first, then assets, so the page depends on tags/status/media
  // and the page size), then navigates there. Falls back to page 1 if the
  // lookup fails.
  const handleGoToAssetFolder = async (asset) => {
    const location = await locateAsset(asset._id);

    const targetFolder = location?.folder ?? asset.folder ?? null;
    const targetPage = location?.page || 1;

    pendingPageRef.current = targetPage;
    clearSelection();
    // Clearing the search (and changing the folder) triggers a single fetch,
    // which the pending page ref steers to the right page.
    setSearchString("");
    setCurrentFolderId(targetFolder);
  };

  // ---- Asset move ---------------------------------------------------------

  const handleMoveAsset = async (asset) => {
    const sourceFolderId = asset.folder ?? null;
    const pick = await openModal(
      <FolderPickerModal
        title="Move to folder"
        initialFolderId={currentFolderId}
        disabledFolderIds={[sourceFolderId]}
        confirmLabel="Move here"
      />,
    );
    if (!pick) return;
    await moveAssetsToFolder([asset._id], pick.folderId, pick.folderName);
  };

  // Move a single folder via the folder-picker modal. We disable the source
  // parent and hide the folder being moved so the user can't pick a no-op
  // or navigate into the moved folder to pick a descendant (cyclic move;
  // backend would also reject).
  const handleMoveFolder = async (folder) => {
    const sourceParentId = folder.parent ?? null;
    const pick = await openModal(
      <FolderPickerModal
        title="Move folder to"
        initialFolderId={currentFolderId}
        disabledFolderIds={[sourceParentId]}
        excludeFolderIds={[folder._id]}
        confirmLabel="Move here"
      />,
    );
    if (!pick) return;
    const targetName = pick.folderName || (pick.folderId ? "folder" : "Root");
    await moveFoldersToParent([folder._id], pick.folderId, targetName);
  };

  // ---- Asset copy ---------------------------------------------------------

  // Copy a single asset. Opens a folder picker (same folder is allowed) and
  // posts a bulk-copy with this single id to the backend.
  const handleCopyAsset = async (asset) => {
    const pick = await openModal(
      <FolderPickerModal
        title="Copy to folder"
        initialFolderId={currentFolderId}
        confirmLabel="Copy here"
      />,
    );
    if (!pick) return;
    await copyAssetsToFolder([asset._id], pick.folderId, pick.folderName);
  };

  // Internal helper: bulk-copy a set of assets into a target folder id.
  // Each new asset gets a " copy" suffix on its name (server-side).
  const copyAssetsToFolder = async (assetIds, folderId, displayName) => {
    addMovingAssets(assetIds);
    try {
      const res = await apiFetch(`/api/shows/${show._id}/assets/bulk-copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetIds, folder: folderId }),
      });
      if (!res.ok) throw new Error("Failed to copy assets");
      await refreshCurrentPage();
      toast.success(
        `Copied ${assetIds.length} item${assetIds.length === 1 ? "" : "s"} to ${displayName}`,
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to copy assets");
    } finally {
      removeMovingAssets(assetIds);
    }
  };

  const handleBulkCopy = async () => {
    if (selectedAssetIds.length === 0) return;
    const pick = await openModal(
      <FolderPickerModal
        title="Copy selected to"
        initialFolderId={currentFolderId}
        confirmLabel="Copy here"
      />,
    );
    if (!pick) return;
    const targetName = pick.folderName || (pick.folderId ? "folder" : "Root");
    await copyAssetsToFolder(selectedAssetIds, pick.folderId, targetName);
    setSelectedAssetIds([]);
  };

  // Internal helper: bulk-move a set of assets into a target folder id (null = root).
  const moveAssetsToFolder = async (assetIds, folderId, displayName) => {
    addMovingAssets(assetIds);
    try {
      const res = await apiFetch(`/api/shows/${show._id}/assets/bulk-move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetIds, folder: folderId }),
      });
      if (!res.ok) throw new Error("Failed to move assets");
      setSelectedAssetIds([]);
      await refreshCurrentPage();
      toast.success(
        `Moved ${assetIds.length} item${assetIds.length === 1 ? "" : "s"} to ${displayName}`,
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to move assets");
    } finally {
      removeMovingAssets(assetIds);
    }
  };

  // Internal helper: move multiple folders into a new parent in parallel.
  // null parentId moves to root.
  const moveFoldersToParent = async (folderIds, parentId, displayName) => {
    if (!folderIds || folderIds.length === 0) return;
    // Don't allow moving a folder into itself (still no descendant check on
    // client; backend rejects cyclic moves).
    const filtered = folderIds.filter((id) => id !== parentId);
    if (filtered.length === 0) return;
    filtered.forEach(addLoadingFolder);
    try {
      const results = await Promise.allSettled(
        filtered.map((id) =>
          apiFetch(`/api/shows/${show._id}/folders/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ parent: parentId }),
          }),
        ),
      );
      const failed = results.filter(
        (r) => r.status === "rejected" || (r.value && !r.value.ok),
      );
      await refreshCurrentPage();
      if (failed.length === 0) {
        toast.success(
          `Moved ${filtered.length} folder${filtered.length === 1 ? "" : "s"} to ${displayName}`,
        );
      } else {
        toast.error(`Failed to move ${failed.length} folder(s)`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to move folders");
    } finally {
      filtered.forEach(removeLoadingFolder);
    }
  };

  // Combined drop handler: moves any mix of assets + folders into a target.
  const dropOnTarget = async ({
    targetFolderId,
    targetName,
    assetIds = [],
    folderIds = [],
  }) => {
    const cleanFolderIds = folderIds.filter((id) => id !== targetFolderId);
    const ops = [];
    if (assetIds.length > 0)
      ops.push(moveAssetsToFolder(assetIds, targetFolderId, targetName));
    if (cleanFolderIds.length > 0)
      ops.push(moveFoldersToParent(cleanFolderIds, targetFolderId, targetName));
    if (ops.length === 0) return;
    // Clear selection so dragged items aren't still highlighted after move
    setSelectedAssetIds([]);
    setSelectedFolderIds([]);
    await Promise.all(ops);
  };

  // Drag-and-drop callback fired by FolderCard. Receives `{ folder, assetIds, folderIds }`.
  const handleDropOnFolder = async ({ folder, assetIds, folderIds }) => {
    await dropOnTarget({
      targetFolderId: folder._id,
      targetName: folder.name,
      assetIds,
      folderIds,
    });
  };

  // Drag-and-drop callback fired by a breadcrumb segment.
  const handleDropOnBreadcrumb = async (
    folderId,
    folderName,
    { assetIds, folderIds },
  ) => {
    await dropOnTarget({
      targetFolderId: folderId,
      targetName: folderName,
      assetIds,
      folderIds,
    });
  };

  // ---- Multi-select operations --------------------------------------------
  const orderSelectionByDisplay = (ids) => {
    const displayIndex = new Map(assets.map((asset, i) => [asset._id, i]));
    return [...ids].sort(
      (a, b) => (displayIndex.get(a) ?? -1) - (displayIndex.get(b) ?? -1),
    );
  };

  const toggleAssetSelected = (asset) => {
    setSelectedAssetIds((prev) =>
      prev.includes(asset._id)
        ? prev.filter((id) => id !== asset._id)
        : orderSelectionByDisplay([...prev, asset._id]),
    );
  };

  const toggleFolderSelected = (folder) => {
    setSelectedFolderIds((prev) =>
      prev.includes(folder._id)
        ? prev.filter((id) => id !== folder._id)
        : [...prev, folder._id],
    );
  };

  const clearSelection = () => {
    setSelectedAssetIds([]);
    setSelectedFolderIds([]);
    setPreviewedAssetId(null);
  };

  // Whether folders count toward selection. When folder selection is
  // disabled, folders are always navigated into (never added to selection).
  const folderSelectable = selectMode && allowSelectFolder;

  const totalSelectedCount = selectedAssetIds.length + selectedFolderIds.length;
  const totalVisibleCount =
    (folderSelectable ? folders.length : 0) + assets.length;
  // Unique mediaTypes among the currently selected assets. Passed down to
  // `AssetItem` so multi-asset drags can advertise every type in the
  // selection via `assetMediaDragType` markers — letting drop targets like
  // AiModelInput pickers accept a drag that contains at least one match and
  // filter the rest at drop time.
  const selectedAssetMediaTypes = useMemo(() => {
    const set = new Set();
    for (const id of selectedAssetIds) {
      const a = assets.find((x) => x._id === id);
      if (a?.mediaType) set.add(a.mediaType);
    }
    return [...set];
  }, [selectedAssetIds, assets]);
  // True when every visible (selectable) item on the current page is
  // selected. Used to toggle the "select all" button into a "deselect all"
  // state.
  const allVisibleSelected =
    totalVisibleCount > 0 &&
    (!folderSelectable ||
      folders.every((f) => selectedFolderIds.includes(f._id))) &&
    assets.every((a) => selectedAssetIds.includes(a._id));

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      clearSelection();
      return;
    }
    if (folderSelectable) {
      setSelectedFolderIds((prev) =>
        Array.from(new Set([...prev, ...folders.map((f) => f._id)])),
      );
    }
    setSelectedAssetIds((prev) =>
      orderSelectionByDisplay(
        Array.from(new Set([...prev, ...assets.map((a) => a._id)])),
      ),
    );
  };

  const handleBulkMove = async () => {
    if (totalSelectedCount === 0) return;
    // Disallow picking any folder where any of the selected items already live.
    // For assets: the asset's `folder` field. For folders: the folder's `parent`.
    // `null` represents root.
    const sourceFolderIds = new Set();
    selectedAssetIds.forEach((id) => {
      const a = assets.find((x) => x._id === id);
      if (a) sourceFolderIds.add(a.folder ?? null);
    });
    selectedFolderIds.forEach((id) => {
      const f = folders.find((x) => x._id === id);
      if (f) sourceFolderIds.add(f.parent ?? null);
    });
    const pick = await openModal(
      <FolderPickerModal
        title="Move selected"
        initialFolderId={currentFolderId}
        disabledFolderIds={Array.from(sourceFolderIds)}
        // Hide the folders being moved themselves so the user can't
        // navigate into them and pick a descendant as the destination
        // (cyclic move; backend would also reject).
        excludeFolderIds={selectedFolderIds}
        confirmLabel="Move here"
      />,
    );
    if (!pick) return;
    await dropOnTarget({
      targetFolderId: pick.folderId,
      targetName: pick.folderName || (pick.folderId ? "folder" : "Root"),
      assetIds: selectedAssetIds,
      folderIds: selectedFolderIds,
    });
  };

  // Shared delete flow for an arbitrary set of asset + folder ids. Shows the
  // destructive confirmation, then archives the assets and deletes the folders
  // in parallel. Used by the select-mode bulk delete and the drag-to-trash zone.
  const deleteItems = async ({ assetIds = [], folderIds = [] }) => {
    if (assetIds.length === 0 && folderIds.length === 0) return;
    const parts = [];
    if (assetIds.length > 0)
      parts.push(`${assetIds.length} asset${assetIds.length === 1 ? "" : "s"}`);
    if (folderIds.length > 0)
      parts.push(
        `${folderIds.length} folder${folderIds.length === 1 ? "" : "s"} (and their contents)`,
      );

    // Assets are archived (recoverable from the archived view); folders are
    // deleted for good. Spell out whichever applies so the confirmation is
    // honest about what's reversible.
    const consequences = [];
    if (assetIds.length > 0)
      consequences.push(
        "Assets will be moved to the archive and can be restored later.",
      );
    if (folderIds.length > 0)
      consequences.push(
        "Folders will be permanently deleted (their assets are archived, not deleted).",
      );

    // Only folders are genuinely deleted, so a selection of assets alone is
    // named for what it does — archive.
    const actionLabel = folderIds.length > 0 ? "Delete" : "Archive";

    const confirm = await openModal(
      <ConfirmModal
        title={`${actionLabel} Selected`}
        type="destructive"
        message={
          <span className="flex flex-col gap-2">
            <span>
              {actionLabel} {parts.join(" and ")}?
            </span>
            {consequences.map((consequence) => (
              <span key={consequence}>{consequence}</span>
            ))}
          </span>
        }
      />,
    );
    if (!confirm) return;
    // Surface the per-card loader on every item being deleted (same overlay
    // as the per-asset / per-folder delete actions).
    addMovingAssets(assetIds);
    folderIds.forEach(addLoadingFolder);
    try {
      await Promise.all([
        ...assetIds.map((id) =>
          apiFetch(`/api/shows/${show._id}/assets/${id}`, {
            method: "PUT",
            body: JSON.stringify({ archived: true }),
          }),
        ),
        ...folderIds.map((id) =>
          apiFetch(`/api/shows/${show._id}/folders/${id}`, {
            method: "DELETE",
          }),
        ),
      ]);
      clearSelection();
      await refreshCurrentPage();
      toast.success(`${actionLabel}d selected items`);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to ${actionLabel.toLowerCase()} some items`);
    } finally {
      removeMovingAssets(assetIds);
      folderIds.forEach(removeLoadingFolder);
    }
  };

  const handleBulkDelete = async () => {
    if (totalSelectedCount === 0) return;
    await deleteItems({
      assetIds: selectedAssetIds,
      folderIds: selectedFolderIds,
    });
  };

  // ---- Drag-to-delete trash zone ------------------------------------------

  const handleTrashDragOver = (e) => {
    const types = e.dataTransfer?.types;
    if (!types) return;
    const arr = Array.from(types);
    if (!arr.includes(ASSET_DRAG_MIME) && !arr.includes(FOLDER_DRAG_MIME))
      return;
    e.preventDefault();
    e.stopPropagation();
    setTrashDragOver(true);
    e.dataTransfer.dropEffect = "move";
  };

  const handleTrashDragLeave = (e) => {
    e.stopPropagation();
    setTrashDragOver(false);
  };

  const handleTrashDrop = async (e) => {
    const assetData = e.dataTransfer.getData(ASSET_DRAG_MIME);
    const folderData = e.dataTransfer.getData(FOLDER_DRAG_MIME);
    if (!assetData && !folderData) return;
    e.preventDefault();
    e.stopPropagation();
    setTrashDragOver(false);
    setIsDraggingItems(false);
    let assetIds = [];
    let folderIds = [];
    try {
      if (assetData) {
        const parsed = JSON.parse(assetData);
        assetIds = Array.isArray(parsed) ? parsed : [];
      }
      if (folderData) {
        const parsed = JSON.parse(folderData);
        folderIds = Array.isArray(parsed) ? parsed : [parsed];
      }
    } catch (err) {
      console.error("Bad drag payload", err);
      return;
    }
    await deleteItems({ assetIds, folderIds });
  };

  // Mirror of `archiveAsset` for the archived view: restores assets into a
  // folder the user picks and resolves `true` only when the restore actually
  // ran. The caller owns the refetch.
  const restoreAssetsFromArchive = async ({
    assetIds,
    notifyOnSuccess = true,
  }) => {
    if (assetIds.length === 0) {
      return false;
    }

    const destination = await openModal(
      <FolderPickerModal
        title="Restore To"
        confirmLabel="Restore"
        initialFolderId={null}
      />,
    );
    if (!destination) {
      return false;
    }

    addMovingAssets(assetIds);
    try {
      const responses = await Promise.all(
        assetIds.map((id) =>
          apiFetch(`/api/shows/${show._id}/assets/${id}`, {
            method: "PUT",
            body: JSON.stringify({
              archived: false,
              folder: destination.folderId,
            }),
          }),
        ),
      );
      if (responses.some((response) => !response.ok)) {
        throw new Error("Failed to restore some assets");
      }
      clearSelection();
      if (notifyOnSuccess) {
        toast.success(
          `Restored ${assetIds.length} asset${assetIds.length === 1 ? "" : "s"} to ${destination.folderName}`,
        );
      }
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Failed to restore some assets");
      return false;
    } finally {
      removeMovingAssets(assetIds);
    }
  };

  const restoreFromArchive = async (assetIds) => {
    const restored = await restoreAssetsFromArchive({ assetIds });
    if (!restored) return;

    // Stay on the current page, as in handleDelete.
    await refreshCurrentPage();
  };

  const destroyAssets = async (assetIds, subject) => {
    if (assetIds.length === 0) {
      return;
    }

    const isSingle = assetIds.length === 1;
    const confirmed = await openModal(
      <ConfirmModal
        title={isSingle ? "Delete Asset" : "Delete Assets"}
        type="destructive"
        message={`Delete ${subject}? This permanently deletes ${
          isSingle ? "it" : "them"
        } and can't be undone.`}
      />,
    );
    if (!confirmed) {
      return;
    }

    addMovingAssets(assetIds);
    try {
      const responses = await Promise.all(
        assetIds.map((id) =>
          apiFetch(`/api/shows/${show._id}/assets/${id}`, {
            method: "DELETE",
          }),
        ),
      );
      if (responses.some((response) => !response.ok)) {
        throw new Error("Failed to delete some assets");
      }
      clearSelection();
      await refreshCurrentPage();
      // A hard delete can never resolve again, so every working-list entry
      // pointing at it has to go. An archive deliberately keeps its entries:
      // it is restorable, and the list can still show it.
      assetIds.forEach((assetId) => removeEntriesForAsset({ assetId }));
      toast.success(
        `Deleted ${assetIds.length} asset${assetIds.length === 1 ? "" : "s"}`,
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete some assets");
    } finally {
      removeMovingAssets(assetIds);
    }
  };

  const handleDestroy = async (asset) => {
    await destroyAssets([asset._id], `'${asset.name}'`);
  };

  const handleBulkDestroy = async () => {
    const count = selectedAssetIds.length;
    await destroyAssets(
      selectedAssetIds,
      `${count} asset${count === 1 ? "" : "s"}`,
    );
  };

  return (
    <DragPreviewProvider>
      <div id="AssetsEditor" className="flex w-full h-full">
        <div className="flex flex-col flex-1 min-w-0 h-full relative @container">
          {/* The region AssetsListAssetPreview overlays. The list strip is a
              sibling below it, which is what keeps the strip anchored and
              usable while the overlay is open. */}
          <div
            id="AssetsEditor__main"
            className="AssetsEditor__main relative flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between @[700px]:px-10 px-2 h-20">
              <div className="flex items-center gap-2 relative">
                {showArchived && (
                  <button
                    type="button"
                    title="Back to all assets"
                    onClick={() => setShowArchived(false)}
                    className="text-secondary-foreground hover:text-white cursor-pointer w-10 flex justify-center items-center rounded-md absolute -left-10">
                    <FaChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <PageTitle>{title}</PageTitle>
                {showArchived && (
                  <span className="text-2xl font-thin">Archive</span>
                )}
                {searching && (
                  <div className="flex items-center text-xs gap-2 text-secondary-foreground/60 ml-4">
                    <LuLoaderCircle className="w-4 h-4 animate-spin" />
                    <span className="text-sm @[700px]:block hidden">
                      Searching...
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="secondary"
                  icon={<FiFolderPlus className="w-4 h-4" />}
                  onClick={handleCreateFolder}>
                  <span className="@[700px]:block hidden">Folder</span>
                </Button>
                <Button
                  type="secondary"
                  icon={<FiPlus className="w-4 h-4" />}
                  onClick={handleCreateSketch}>
                  <span className="@[700px]:block hidden">Create</span>
                </Button>
                <Button
                  type="secondary"
                  icon={<FiBox className="w-4 h-4" />}
                  onClick={handleCreateScene3D}>
                  <span className="@[700px]:block hidden">3D</span>
                </Button>
                {allowUpload && (
                  <Button
                    type="secondary"
                    icon={<FiUpload className="w-4 h-4" />}
                    onClick={handleUploadClick}>
                    <span className="@[700px]:block hidden">Upload</span>
                  </Button>
                )}
                {allowUpload && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={mediaTypes}
                    className="hidden"
                    onChange={handleFileInputChange}
                  />
                )}
              </div>
            </div>

            {/* Filter toolbar — search input + tags / status dropdowns */}
            <div className="flex items-start @[700px]:items-center gap-1 @[700px]:px-10 px-2 @[700px]:flex-row flex-col">
              {!showArchived && (
                <button
                  id="AssetsEditor__toggleAiPanel"
                  type="button"
                  title="Generate"
                  onClick={() => setShowAiModelInput((v) => !v)}
                  className={`hidden @[700px]:inline-flex h-9 outline-none items-center px-2 text-xs rounded-md cursor-pointer border ${showAiModelInput ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary text-foreground"}`}>
                  <LuSparkles className="w-4 h-4" />
                </button>
              )}
              <div className="relative @[700px]:w-58 w-full">
                <Input
                  type="text"
                  placeholder="Search"
                  className={`text-xs ${searchString ? "pr-14" : "pr-9"}`}
                  onChange={(e) => setSearchString(e.target.value)}
                  value={searchString}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    title={
                      searchInFolder
                        ? "Searching in current folder — click for global"
                        : "Searching globally — click to scope to current folder"
                    }
                    onClick={() => setSearchInFolder((v) => !v)}
                    className={`p-1 rounded-sm cursor-pointer ${
                      searchInFolder
                        ? "text-primary hover:text-primary/80"
                        : "text-secondary-foreground/50 hover:text-secondary-foreground"
                    }`}>
                    <FiFolder className="w-4 h-4" />
                  </button>
                  {searchString && (
                    <FiX
                      className="w-4 h-4 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer"
                      onClick={() => setSearchString("")}
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 flex-1 w-full">
                {!showArchived && (
                  <button
                    id="AssetsEditor__toggleAiPanelCompact"
                    type="button"
                    title="Generate"
                    onClick={() => setShowAiModelInput((v) => !v)}
                    className={`inline-flex @[700px]:hidden h-9 outline-none items-center px-2 text-xs rounded-md cursor-pointer border ${showAiModelInput ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary text-foreground"}`}>
                    <LuSparkles className="w-4 h-4" />
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                      <FiTag className="w-4 h-4 @[700px]:hidden" />
                      <span className="@[700px]:block hidden">Tags</span>
                      {!allTagsSelected ? (
                        <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                          {selectedFilterTags.length + (showUntagged ? 1 : 0)}
                        </span>
                      ) : (
                        <FiChevronDown className="w-4 h-4 text-secondary-foreground/60 @[700px]:block hidden" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <button
                      type="button"
                      className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
                      onClick={() => {
                        setSelectedFilterTags(
                          show.asset_categories.map((tag) => tag._id),
                        );
                        setShowUntagged(true);
                      }}>
                      Select all
                    </button>
                    <DropdownMenuSeparator />
                    {show.asset_categories.map((tag) => (
                      <DropdownMenuCheckboxItem
                        key={tag._id}
                        checked={selectedFilterTags.includes(tag._id)}
                        onCheckedChange={(checked) =>
                          handleFilterTagChange(tag._id, checked)
                        }
                        onSelect={(e) => e.preventDefault()}
                        className="group capitalize text-xs h-6">
                        <span className="flex-1">{tag.name}</span>
                        <button
                          type="button"
                          className="ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedFilterTags([tag._id]);
                            setShowUntagged(false);
                          }}>
                          Only
                        </button>
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={showUntagged}
                      onCheckedChange={(checked) => setShowUntagged(checked)}
                      onSelect={(e) => e.preventDefault()}
                      className="text-xs h-6">
                      Untagged
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Media type filter */}
                {allowedMediaPrefixes.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                        <MdOutlinePermMedia className="w-4 h-4 @[700px]:hidden" />
                        <span className="@[700px]:block hidden">Media</span>
                        {!allMediaSelected ? (
                          <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                            {selectedMediaCount}
                          </span>
                        ) : (
                          <FiChevronDown className="w-4 h-4 text-secondary-foreground/60 @[700px]:block hidden" />
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <button
                        type="button"
                        className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
                        onClick={() =>
                          setSelectedMediaTypes(allowedMediaPrefixes)
                        }>
                        Select all
                      </button>
                      <DropdownMenuSeparator />
                      {allowedMediaPrefixes.map((prefix) => (
                        <DropdownMenuCheckboxItem
                          key={prefix}
                          checked={selectedMediaTypes.includes(prefix)}
                          onCheckedChange={(checked) =>
                            setSelectedMediaTypes((prev) =>
                              checked
                                ? Array.from(new Set([...prev, prefix]))
                                : prev.filter((t) => t !== prefix),
                            )
                          }
                          onSelect={(e) => e.preventDefault()}
                          className="group capitalize text-xs h-6">
                          <span className="flex-1">{prefix}</span>
                          <button
                            type="button"
                            className="ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedMediaTypes([prefix]);
                            }}>
                            Only
                          </button>
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Sort order */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="select-none inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                      <MdSort className="w-4 h-4" />
                      <span className="@[700px]:block hidden whitespace-nowrap">
                        {
                          (
                            ASSET_SORT_DROPDOWN_OPTIONS.find(
                              (o) => o.value === sort,
                            ) || ASSET_SORT_DROPDOWN_OPTIONS[0]
                          ).label
                        }
                      </span>
                      <FiChevronDown className="w-3 h-3 text-secondary-foreground/60 @[700px]:block hidden" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-40">
                    <DropdownMenuRadioGroup
                      value={sort}
                      onValueChange={setSort}>
                      {ASSET_SORT_DROPDOWN_OPTIONS.map((option) => (
                        <DropdownMenuRadioItem
                          key={option.value}
                          value={option.value}
                          className="text-xs h-6">
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                {(show.characters || []).length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                        <FiUser className="w-4 h-4 @[700px]:hidden" />
                        <span className="@[700px]:block hidden">
                          Characters
                        </span>
                        {selectedCharacterIds.length > 0 ? (
                          <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                            {selectedCharacterIds.length}
                          </span>
                        ) : (
                          <FiChevronDown className="w-4 h-4 text-secondary-foreground/60 @[700px]:block hidden" />
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="w-56 max-h-72 overflow-y-auto">
                      <button
                        type="button"
                        className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
                        onClick={() => setSelectedCharacterIds([])}>
                        All assets
                      </button>
                      <DropdownMenuSeparator />
                      {show.characters.map((character) => (
                        <DropdownMenuCheckboxItem
                          key={character._id}
                          checked={selectedCharacterIds.includes(character._id)}
                          onCheckedChange={(checked) =>
                            setSelectedCharacterIds((prev) =>
                              checked
                                ? Array.from(new Set([...prev, character._id]))
                                : prev.filter((id) => id !== character._id),
                            )
                          }
                          onSelect={(e) => e.preventDefault()}
                          className="group capitalize text-xs h-6">
                          <span className="flex-1">{character.name}</span>
                          <button
                            type="button"
                            className="ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCharacterIds([character._id]);
                            }}>
                            Only
                          </button>
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Folder-wide actions. Both need a folder to scope to, so at the
                  root they stay visible but disabled rather than disappearing. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      id="AssetsEditor__folderActionsTrigger"
                      type="button"
                      title="Folder actions"
                      className="select-none inline-flex h-9 w-9 outline-none items-center justify-center text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                      <FiMoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      className="text-xs p-2"
                      onClick={handleBulkRenameFolder}
                      disabled={!allowSelect || !currentFolderId}>
                      <CgArrowsExchange className="w-4 h-4" />
                      Rename all in folder
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-xs p-2"
                      onClick={handleDownloadCurrentFolder}
                      disabled={!currentFolderId || zipExporting}>
                      {zipExporting ? (
                        <LuLoaderCircle className="w-4 h-4 animate-spin" />
                      ) : (
                        <FiDownload className="w-4 h-4" />
                      )}
                      Download folder
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      id="AssetsEditor__toggleArchived"
                      className="text-xs p-2"
                      onClick={() => setShowArchived((v) => !v)}>
                      <FiArchive className="w-4 h-4" />
                      {showArchived ? "Hide archived" : "Show archived"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex items-center">
                  {allowSelect &&
                    (selectMode ? (
                      <Button
                        id="AssetsEditor__selectModeDone"
                        type="primary"
                        className="h-9 rounded-r-none"
                        icon={<MdOutlineCheckBox className="w-4 h-4" />}
                        onClick={() => {
                          setSelectMode(false);
                          clearSelection();
                        }}>
                        <span className="@[700px]:block hidden">Done</span>
                      </Button>
                    ) : (
                      <button
                        id="AssetsEditor__selectMode"
                        type="button"
                        className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer"
                        onClick={() => {
                          setSelectMode(true);
                          clearSelection();
                        }}>
                        <MdOutlineCheckBox className="w-4 h-4" />
                        <span className="@[700px]:block hidden">Select</span>
                      </button>
                    ))}
                  {allowSelect && selectMode && (
                    <TooltipProvider>
                      <div className="flex items-center h-9 px-1 bg-secondary/50 rounded-md rounded-l-none">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={selectActionBtn}
                              onClick={handleSelectAll}
                              disabled={totalVisibleCount === 0}>
                              <MdSelectAll className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {allVisibleSelected
                              ? "Deselect all on page"
                              : "Select all on page"}
                          </TooltipContent>
                        </Tooltip>
                        {!showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectActionBtn}
                                onClick={handleBulkMove}
                                disabled={totalSelectedCount === 0}>
                                <MdDriveFileMoveOutline className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Move</TooltipContent>
                          </Tooltip>
                        )}
                        {!showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectActionBtn}
                                onClick={handleBulkCopy}
                                disabled={selectedAssetIds.length === 0}>
                                <FiCopy className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Copy</TooltipContent>
                          </Tooltip>
                        )}
                        {!showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                id="AssetsEditor__assignTags"
                                type="button"
                                className={selectActionBtn}
                                onClick={handleAssignTags}
                                disabled={selectedAssetIds.length === 0}>
                                <GoTag className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Assign Tags</TooltipContent>
                          </Tooltip>
                        )}
                        {!showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectActionBtn}
                                onClick={handleAssignCharacters}
                                disabled={selectedAssetIds.length === 0}>
                                <FiUser className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Assign Characters</TooltipContent>
                          </Tooltip>
                        )}
                        {!showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectActionBtn}
                                onClick={handleBulkRename}
                                disabled={selectedAssetIds.length === 0}>
                                <CgArrowsExchange className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Bulk Rename Selection
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={selectActionBtn}
                              onClick={handleDownloadSelection}
                              disabled={
                                selectedAssetIds.length === 0 || zipExporting
                              }>
                              {zipExporting ? (
                                <LuLoaderCircle className="w-3 h-3 animate-spin" />
                              ) : (
                                <FiDownload className="w-3 h-3" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Download Selection</TooltipContent>
                        </Tooltip>
                        {showArchived && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectRestoreBtn}
                                onClick={() =>
                                  restoreFromArchive(selectedAssetIds)
                                }
                                disabled={selectedAssetIds.length === 0}>
                                <MdRestoreFromTrash className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Restore</TooltipContent>
                          </Tooltip>
                        )}

                        {showArchived ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectDangerBtn}
                                onClick={handleBulkDestroy}
                                disabled={selectedAssetIds.length === 0}>
                                <MdDeleteForever className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={selectDangerBtn}
                                onClick={handleBulkDelete}
                                disabled={totalSelectedCount === 0}>
                                <MdDeleteOutline className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Archive</TooltipContent>
                          </Tooltip>
                        )}

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={selectActionBtn}
                              onClick={clearSelection}
                              disabled={totalSelectedCount === 0}>
                              <FiX className="w-3 h-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Clear</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  )}
                </div>

                {/* Pagination */}
                <div className="flex-1 justify-end flex items-center gap-1">
                  <div className="flex items-center border rounded-md h-9 overflow-hidden">
                    <button
                      id="AssetsEditor__firstPage"
                      type="button"
                      onClick={handleFirstPage}
                      disabled={page === 1}
                      title="First page"
                      className="inline-flex h-9 px-0.5 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
                      <FaAngleDoubleLeft className="w-3 h-3" />
                    </button>
                    <button
                      id="AssetsEditor__previousPage"
                      type="button"
                      onClick={handlePreviousPage}
                      disabled={page === 1}
                      title="Previous page"
                      className="inline-flex h-9 px-0.5 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
                      <FaAngleLeft className="w-3 h-3" />
                    </button>
                    <div className="text-xs text-secondary-foreground tabular-nums flex items-center px-1">
                      {/* The id sits on the page number alone: a wrapper would
                          read "3/9" and a test asserting the current page would
                          have to parse it back apart. */}
                      <span id="AssetsEditor__pageIndicator">{page}</span>
                      {/* Reads "/9", not "9" — the slash is part of the text. */}
                      <span
                        id="AssetsEditor__pageCount"
                        className="@[700px]:block hidden block">
                        /{totalPages}
                      </span>
                    </div>
                    <button
                      id="AssetsEditor__nextPage"
                      type="button"
                      onClick={handleNextPage}
                      disabled={page === totalPages}
                      className="inline-flex h-9 px-0.5 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
                      <FaAngleRight className="w-3 h-3" />
                    </button>
                  </div>
                  {/* <span className='text-xs text-secondary-foreground tabular-nums'>{`${((page - 1) * pageSize) + 1}-${((page - 1) * pageSize) + pageSize > totalAssets ? totalAssets : ((page - 1) * pageSize) + pageSize} of ${totalAssets}`}</span> */}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="select-none inline-flex h-9 outline-none items-center gap-1 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                        <div className="flex items-center gap-1">
                          <span>{pageSize}</span>
                          <span className="@[700px]:block hidden whitespace-nowrap">
                            {" "}
                            / page
                          </span>
                        </div>
                        <FiChevronDown className="w-3 h-3 text-secondary-foreground/60 @[700px]:block hidden" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                      <DropdownMenuRadioGroup
                        value={String(pageSize)}
                        onValueChange={(v) => setPageSize(parseInt(v))}>
                        {[20, 50, 100].map((size) => (
                          <DropdownMenuRadioItem
                            key={size}
                            value={String(size)}
                            className="text-xs h-6">
                            {size} / page
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center rounded-md border overflow-hidden h-9">
                    <button
                      type="button"
                      onClick={() =>
                        setGridCols((c) => Math.max(MIN_GRID_COLS, c - 1))
                      }
                      disabled={gridCols <= MIN_GRID_COLS}
                      className="inline-flex h-9 w-9 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
                      <FiZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setGridCols((c) => Math.min(MAX_GRID_COLS, c + 1))
                      }
                      disabled={gridCols >= MAX_GRID_COLS}
                      className="inline-flex h-9 w-9 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background border-l">
                      <FiZoomOut className="w-4 h-4" />
                    </button>
                  </div>

                  <TasksSidebarToggle sidebar={tasksSidebar} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 @[700px]:px-10 px-2 text-xs text-secondary-foreground h-8">
              {showArchived === false && isSearching && (
                <span className="font-medium">Search results</span>
              )}
              {showArchived === false && isSearching === false && (
                <>
                  <BreadcrumbSegment
                    isCurrent={!currentFolderId}
                    onClick={() => handleNavigateToFolder(null)}
                    onDrop={(payload) =>
                      handleDropOnBreadcrumb(null, "Root", payload)
                    }>
                    Root
                  </BreadcrumbSegment>
                  {ancestors.map((a, i) => (
                    <span
                      key={a._id}
                      className="flex items-center gap-2 truncate max-w-[200px]">
                      <span className="text-secondary-foreground/50">/</span>
                      <BreadcrumbSegment
                        isCurrent={i === ancestors.length - 1}
                        onClick={() => handleNavigateToFolder(a._id)}
                        onDrop={(payload) =>
                          handleDropOnBreadcrumb(a._id, a.name, payload)
                        }>
                        {a.name}
                      </BreadcrumbSegment>
                    </span>
                  ))}
                </>
              )}
            </div>

            <ResizablePanelGroup
              direction="horizontal"
              className="flex-1 flex overflow-hidden relative">
              {!showArchived && (
                <ResizablePanel
                  id="AiModelInput__ResizePanel"
                  panelRef={aiPanelRef}
                  collapsible
                  collapsedSize={0}
                  defaultSize={450}
                  minSize={350}
                  maxSize={1000}
                  // Dragging the handle past minSize collapses the panel too
                  // (react-resizable-panels' own collapsible behavior) — keep
                  // showAiModelInput (and its toggle button) in sync with that,
                  // not just with our own expand()/collapse() calls.
                  onResize={() => {
                    const panel = aiPanelRef.current;
                    if (!panel) return;
                    setShowAiModelInput(!panel.isCollapsed());
                  }}
                  className="AiModelInput__ResizePanelContent py-2 @[700px]:pl-10 pl-2 ">
                  <AiModelInput
                    ref={aiInputRef}
                    onTaskSubmitted={() => {
                      refreshCurrentPageIfFirstPage();
                      tasksSidebar.refresh();
                    }}
                    resolveAssetById={(id) =>
                      assets.find((a) => a._id === id) || null
                    }
                    destinationFolder={currentFolderId}
                  />
                </ResizablePanel>
              )}

              <ResizableHandle
                className={`w-2 h-full bg-transparent ${showAiModelInput ? "" : "w-0 pointer-events-none"}`}
              />
              <ResizablePanel
                className="relative flex flex-col flex-1 overflow-hidden"
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}>
                <div
                  ref={scrollContainerRef}
                  className={`flex flex-col min-h-0 pb-10 flex-1 overflow-y-auto py-2 px-2 ${showAiModelInput && !showArchived ? "@[700px]:pr-10" : "@[700px]:px-10"}`}
                  onClick={() => setPreviewedAssetId(null)}>
                  <div
                    className="text-white w-full grid @[700px]:gap-4 gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                    }}>
                    {folders.map((folder) => (
                      <FolderCard
                        key={folder._id}
                        folder={folder}
                        onOpen={handleOpenFolder}
                        onRename={handleRenameFolder}
                        onInlineRename={handleInlineRenameFolder}
                        onDelete={handleDeleteFolder}
                        onMove={handleMoveFolder}
                        onDownload={handleDownloadFolder}
                        onToggleVisibility={handleToggleFolderVisibility}
                        onAssetDrop={handleDropOnFolder}
                        busy={loadingFolderIds.includes(folder._id)}
                        selectable={folderSelectable}
                        selected={selectedFolderIds.includes(folder._id)}
                        onToggleSelect={toggleFolderSelected}
                        selectedFolderIds={selectedFolderIds}
                        selectedAssetIds={selectedAssetIds}
                        draggingFolderIds={draggingFolderIds}
                        onBeginFolderDrag={beginFolderDrag}
                        onItemDragStart={beginItemDrag}
                        onEditingChange={handleItemEditingChange}
                      />
                    ))}
                    {tasks.map((task) => (
                      <TaskCard key={task._id} task={task} />
                    ))}
                    {assets.map((asset) => (
                      <AssetItem
                        key={asset._id}
                        asset={asset}
                        onEdit={handleEditAsset}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onPreview={handleAssetsPreview}
                        onRefreshAsset={refreshAsset}
                        onAgent={handleAgent}
                        onCompleteDraft={handleCompleteDraft}
                        onSketchEdit={handleOpenSketchEditor}
                        onScene3DEdit={handleOpenScene3DEditor}
                        onMove={handleMoveAsset}
                        onCopy={handleCopyAsset}
                        onRecallSettings={handleRecallSettings}
                        onAddToList={handleAddAssetToList}
                        onInlineRename={handleInlineRenameAsset}
                        onGoToFolder={handleGoToAssetFolder}
                        showGoToFolder={isSearching}
                        selectable={selectMode}
                        selected={selectedAssetIds.includes(asset._id)}
                        previewed={
                          !selectMode && previewedAssetId === asset._id
                        }
                        onToggleSelect={toggleAssetSelected}
                        selectedIds={selectedAssetIds}
                        selectedFolderIds={selectedFolderIds}
                        selectedAssetMediaTypes={selectedAssetMediaTypes}
                        busy={movingAssetIds.includes(asset._id)}
                        onBeginFolderDrag={beginFolderDrag}
                        onItemDragStart={beginItemDrag}
                        onRestoreFromArchive={restoreFromArchive}
                        onDestroy={handleDestroy}
                        onEditingChange={handleItemEditingChange}
                      />
                    ))}
                  </div>

                  {!isSearching &&
                    folders.length === 0 &&
                    assets.length === 0 &&
                    tasks.length === 0 &&
                    !searching && (
                      <div className="flex-1 flex items-center justify-center text-secondary-foreground/60 text-sm">
                        This folder is empty. Drop files to upload, or create a
                        new folder.
                      </div>
                    )}
                </div>

                {isDragOver && (
                  <div className="absolute top-1 left-1 right-1 bottom-1 z-40 pointer-events-none rounded-lg border-2 border-dashed border-primary bg-card/90 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-primary">
                      <FiUploadCloud className="w-12 h-12" />
                      <span className="text-base font-medium">
                        Drop your files here
                      </span>
                    </div>
                  </div>
                )}
              </ResizablePanel>
              {/* Drag-to-delete target — only mounted while dragging items.
                  `absolute` within ResizablePanelGroup (itself `relative`),
                  not `fixed` to the viewport — the AssetsList strip docks
                  below this group as a sibling, and a viewport-fixed target
                  would sit on top of it instead of clearing it. */}
              {isDraggingItems && (
                <div
                  className="absolute bottom-8 right-8 z-50 flex flex-col items-center gap-2 select-none"
                  onDragOver={handleTrashDragOver}
                  onDragLeave={handleTrashDragLeave}
                  onDrop={handleTrashDrop}>
                  <div
                    className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-150 bg-destructive-foreground text-secondary-foreground ${
                      trashDragOver ? "scale-110" : ""
                    }`}>
                    <MdDeleteOutline className="w-6 h-6" />
                  </div>
                </div>
              )}

              {loading && (
                <Loader
                  status={loadingStatus}
                  showProgress={isUploading}
                  progress={uploadProgress}
                />
              )}
            </ResizablePanelGroup>

            {showAssetsList && (
              <AssetsListAssetPreviewContainer
                resolveAssetById={(id) =>
                  assets.find((a) => a._id === id) || null
                }
              />
            )}
          </div>

          {showAssetsList && (
            <AssetsListContainer
              resolveAssetById={(id) =>
                assets.find((a) => a._id === id) || null
              }
            />
          )}
        </div>

        <TasksSidebarPanel sidebar={tasksSidebar} />
        {sketchEditorState && (
          <div className="absolute inset-0 z-50 bg-background">
            <DragPreviewProvider>
              <SketchEditor
                sketchId={sketchEditorState.sketchId}
                initialImageUrl={sketchEditorState.initialImageUrl}
                initialName={sketchEditorState.initialName}
                onClose={handleCloseSketchEditor}
                onSaveComplete={handleSketchSaveComplete}
              />
            </DragPreviewProvider>
          </div>
        )}
      </div>
    </DragPreviewProvider>
  );
}
